<template>
  <div class="maps-popup" tabindex="-1" ref="popup">
    <div class="maps-popup__left">
      <img :src="imageUrl" :class="imageClasses" />
      <div class="maps-popup__title">
        {{ nickName }}
      </div>

      <div v-if="!isMe && publicContactPhoneNumber" class="maps-popup__contact">
        <LinkButton
          @click="messagingService.openWhatsAppWithNumber(publicContactPhoneNumber)"
          :text="'Kontaktieren'"
          icon-left="WhatsApp"
          :extra-classes="['button--whatsapp']"
          target="_blank"
          :icon-classes="[]"
        />
        <span>
          <UnicodeIcon :bold="true" size="1.2rem" color="black" icon="ⓘ" />
          <span>Tausche dich direkt über WhatsApp mit diesem eFriend aus</span>
        </span>
      </div>

      <div class="maps-popup__description" v-if="descriptionText && descriptionTextPosition === 'left'" v-html="formatText(descriptionText)"></div>
    </div>
    <div class="maps-popup__right">
      <div class="maps-popup__connect" v-if="!isMe">
        <template v-if="isTour">
          <LinkButton @click.native="addToPortfolio" icon-left="Bolt" :text="t('efriends.doConnectButton')" v-if="!isInPortfolio"
                      :extra-classes="['maps-popup__connect-button']" />
          <LinkButton @click.native="removeFromPortfolio" :text="t('efriends.dontConnectButton')" icon-left="SignOut" v-if="isInPortfolio"
                      :extra-classes="['maps-popup__connect-button']" />
        </template>
        <template v-else-if="isEnergyCommunityOrPeer2Peer == false">
          <LinkButton v-if="canReceiveDonations && !isDonatee && iHaveProduction" @click.native="onDonateToCubeClick()" :text="t('efriends.doDonateButton')"
                      :form="`donateToCube_${cubeName}`" icon-left="HeartOutline" :extra-classes="[
              'maps-popup__connect-button',
              'maps-popup__connect-button--donate',
            ]" />
          <LinkButton v-if="canReceiveDonations && isDonatee" @click.native="onStopDonatingToCubeClick()" :text="t('efriends.stopDonatingButton')"
                      :form="`stopDonatingToCube_${cubeName}`" icon-left="HeartFull" :extra-classes="[
              'maps-popup__connect-button',
              'maps-popup__connect-button--stop-donating',
            ]" />
          <LinkButton v-if="canConnectToProducer" @click.native="onConnectToCubeClick()" :text="t('efriends.doConnectButton')"
                      :form="`connectToCube_${cubeName}`" icon-left="Bolt" :extra-classes="['maps-popup__connect-button']" />
        </template>
      </div>

      <div class="maps-popup__production-type" v-if="hasProduction">
        {{ t(`productionTypes.${productionType}`, null, productionType?.toUpperCase()) }}<br />
      </div>
      <div class="maps-popup__capacity" v-if="hasProduction">
        <div class="maps-popup__label">{{ t('efriends.capacity') }}</div>
        {{ productionCapacity }}

        <template v-if="inverterPublicUrl || allowDisplayProductionDetails">
          <div class="maps-popup__production-data">
            <template v-if="allowDisplayProductionDetails">
              <div class="maps-popup__label">{{ t('efriends.mapsPopup.productionDataLabel') }}</div>
            </template>

            <output class="maps-popup__production-data-table" v-if="allowDisplayProductionDetails">
              {{ t('efriends.mapsPopup.twoHourAvg') }} <strong>{{ productionAvgTwoHours }}</strong><br />
              {{ t('efriends.mapsPopup.surplusYesterdayLabel') }} <strong>{{ surplusYesterday }}</strong><br />
              {{ t('efriends.mapsPopup.surplusLastMonthLabel') }} <strong>{{ surplusLastMonth }}</strong><br />
            </output>
            <output class="maps-popup__production-data-table__link" v-if="inverterPublicUrl">
              <a :href="inverterPublicUrl" target="_blank">{{ t('efriends.mapsPopup.productionDetailsLink') }}</a>
            </output>
          </div>
        </template>
      </div>
      <div class="maps-popup__prices" v-if="hasProduction">
        <output class="maps-popup__prices-current">
          <Price :label="t('efriends.currentPrice') + ' (brutto)'" :value="currentPrice" />
        </output>
        <output class="maps-popup__prices-next" v-if="nextPrice !== false">
          <Price :label="nextPriceLabel" :value="nextPrice" />
        </output>
      </div>

      <div class="maps-popup__grid-fee-discount" v-if="showGridFeeDiscountIcon">
        <img class="maps-popup__grid-fee-discount-icon" :src="assetPath('netzkostenersparnis.svg')" :alt="gridFeeDiscountLabel" :title="gridFeeDiscountLabel" />
        <span class="maps-popup__grid-fee-discount__label">
          {{ gridFeeDiscountLabel }}
        </span>
      </div>

      <div class="maps-popup__energy-lines" v-if="(hasProduction || isProducerToMe) && !isTour">
        <div class="maps-popup__label">{{ t('efriends.mapsPopup.energylinesLabel') }}</div>
        {{ energyLinesRequired }} Energyline{{ energyLinesRequired > 1 ? 's' : '' }}
        <template v-if="isProducerToMe"> {{ t('efriends.mapsPopup.energylinesUsed') }} </template>
        <template v-else> {{ t('efriends.mapsPopup.energylinesNeeded') }} </template>
        <template v-if="!isProducerToMe && canAddConsumers && !hasEnoughUnusedEnergyLinesToConnectTo">
          <div class="maps-popup__energy-lines-note">
            <template v-if="unusedEnergyLines > 0">
              {{ t('efriends.mapsPopup.energylinesLow', { '@count': unusedEnergyLines, '@unit': unusedEnergyLines > 1 ? 'Energylines' : 'Energyline' }) }}
            </template>
            <template v-else>
              <template v-if="isCommunityless">
                {{ t('efriends.mapsPopup.energylines.communitylessHint') }}
              </template>
              <template v-else>
                {{ t('efriends.mapsPopup.energylinesNone') }}
              </template>
            </template>
          </div>
        </template>
      </div>

      <div class="maps-popup__available-connections" v-if="hasProduction && !isTour">
        <template v-if="openConnectionsAvailable > 0">
          {{ t('efriends.mapsPopup.householdsCanConnect', { '@count': openConnectionsAvailable }) }}
        </template>
        <template v-else-if="openConnectionsAvailable < 0">
          {{ t('efriends.mapsPopup.comingSoon') }}
        </template>
        <template v-else>
          {{ t('efriends.mapsPopup.allConnectionsTaken') }}
        </template>
      </div>

      <div class="maps-popup__description" v-if="descriptionText && descriptionTextPosition === 'main'" v-html="formatText(descriptionText)">
      </div>

      <div class="maps-popup__hearts" v-if="hasProduction">
        <button class="maps-popup__hearts__button" @click="toggleLike" :disabled="!isLikedByMe && !isLikeable">
          <HeartFull v-if="!isLikeable && numLikes > 0" :extra-classes="likeIconClasses" />
          <HeartOutline v-else-if="isLikeable && !isLikedByMe" :extra-classes="likeIconClasses" />
          <template v-if="numLikes > 0">
            {{ t('efriends.mapsPopup.likedBy', { '@count': numLikes, '@unit': numLikes > 1 ? 'eFriends' : 'eFriend' }) }}
          </template>
          <template v-else>
            {{ t('efriends.zeroFriends') }}
          </template>
        </button>
      </div>

      <div v-if="isConnected && !isMe" class="maps-popup__connections">
        <ConsumptionArrow classes="maps-popup__is-producer" type="positive" :angle="180" v-if="isProducerToMe" />
        <ConsumptionArrow classes="maps-popup__is-consumer" type="negative" v-if="isConsumerOfMe && !isDonatee" />
        <HeartFull v-if="isDonatee || isContributor" :extra-classes="['maps-popup__donates']" />
        <div class="maps-popup__connections-text">
          {{ connectionText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import ConsumptionArrow from '@/components/icons/ConsumptionArrow.vue';
  import LinkButton from '@/components/LinkButton.vue';
  import Price from '@/components/Price.vue';
  import HeartFull from '@/components/icons/HeartFull.vue';
  import HeartOutline from '@/components/icons/HeartOutline.vue';
  import { useRouter, useRoute } from 'vue-router';
  import { useStrings } from '@/composables/useStrings.ts';
  import { useMapStore } from '@/stores/useMapStore';
  import { useCubeStore } from '@/stores/useCubeStore';
  import { useDate } from '@/composables/useDate.ts';
  import { computed, toRef, useTemplateRef } from 'vue';
  import { useSpecificCube } from '@/composables/useSpecificCube.ts';
  import { assetPath } from '@/util';
  import { useMyCube } from '@/composables/useMyCube.js';
  import { useHtmlUtil } from '@/composables/useHtmlUtil';
  import { useAccountStore } from '@/stores/useAccountStore';
  import { useMessagingService } from '@/composables/useMessagingService';
  import UnicodeIcon from '@/components/icons/UnicodeIcon.vue';

  const props = defineProps({
    infoWindow: {
      type: Object,
    },
    cubeName: {
      type: String,
      required: true,
    },
  });

  const mapStore = useMapStore();
  const cubeStore = useCubeStore();
  const { formatText } = useHtmlUtil();
  const router = useRouter();
  const { t } = useStrings();
  const isTour = computed(() => router.currentRoute.value.name === 'tour');
  const cubeName = toRef(() => props.cubeName);
  const cube = computed(() => mapStore.getCubeByName(cubeName.value, isTour.value ? 'tour' : 'global'));
  const myCube = useMyCube();
  const accountStore = useAccountStore();

  const {
    nickName,
    isMe,
    isConnected,
    isDonatee,
    isProducerToMe,
    isContributor,
    isConsumerOfMe,
    hasProduction,
    canReceiveDonations,
    hasThumbnail,
    isLikeable,
    isLikedByMe,
    imageUrl,
    descriptionText,
    addToPortfolio,
    isInPortfolio,
    removeFromPortfolio,
    onDonateToCubeClick,
    onStopDonatingToCubeClick,
    canConnectToProducer,
    onConnectToCubeClick,
    productionType,
    productionCapacity,
    inverterPublicUrl,
    publicContactPhoneNumber,
    allowDisplayProductionDetails,
    productionAvgTwoHours,
    surplusYesterday,
    surplusLastMonth,
    currentPrice,
    nextPrice,
    nextPriceLabel,
    energyLinesRequired,
    canAddConsumers,
    hasEnoughUnusedEnergyLinesToConnectTo,
    openConnectionsAvailable,
    numLikes,
    toggleLike,
    gridFeeDiscountApplies,
    gridFeeDiscountPriceBrutto
  } = useSpecificCube(router, cube);

  const messagingService = useMessagingService();

  const gridFeeDiscountLabel = computed(() => {
    return t('efriends.gridFeeDiscountApplies', { '@price': gridFeeDiscountPriceBrutto.value });
  });

  const showGridFeeDiscountIcon = computed(() => {
    return myCube.isGrantedGridFeeDiscount.value && gridFeeDiscountApplies.value;
  });

  const unusedEnergyLines = computed(() => cubeStore.unusedEnergyLines);
  const iHaveProduction = computed(() => cubeStore.hasProduction);
  const isCommunityless = computed(() => accountStore.properties.isCommunityless);

  const connectionText = computed(() => {
    if (isDonatee.value) {
      if (isProducerToMe.value) {
        // The current cube donates power to and consumes from the other cube.
        return t(
          'efriends.mapsPopup.connectionTextDonateAndConsume',
          {
            '@cube': nickName.value,
          }
        );
      }
      else {
        // The current cube donates power to the other cube.
        return t('efriends.mapsPopup.connectionTextDonate', {
          '@cube': nickName.value,
        });
      }
    }
    else if (isContributor.value) {
      if (isConsumerOfMe.value) {
        // This cube donates power to us and consumes from us.
        return t(
          'efriends.mapsPopup.connectionTextContributesAndConsume',
          {
            '@cube': nickName.value,
          }
        );
      }
      else {
        // This cube donates to us.
        return t('efriends.mapsPopup.connectionTextContributes', {
          '@cube': nickName.value,
        });
      }
    }
    else {
      // No donating power in either direction.
      if (isProducerToMe.value && isConsumerOfMe.value) {
        return t('efriends.mapsPopup.connectionTextBidirectional', {
          '@cube': nickName.value,
        });
      }
      else if (isProducerToMe.value) {
        return t('efriends.mapsPopup.connectionTextProducer', {
          '@cube': nickName.value,
        });
      }
      else if (isConsumerOfMe.value) {
        return t('efriends.mapsPopup.connectionTextConsumer', {
          '@cube': nickName.value,
        });
      }
      else {
        return '';
      }
    }
  });

  const descriptionTextPosition = computed(() => {
    if (hasProduction.value || canReceiveDonations.value) {
      return 'left';
    }
    return 'main';
  });

  const imageClasses = {
    'maps-popup__img': true,
    'maps-popup__img--thumbnail': hasThumbnail.value,
  };

  const likeIconClasses = computed(() => {
    const result = [
      'maps-popup__hearts-icon',
    ];

    if (isLikeable.value || isLikedByMe.value) {
      result.push('maps-popup__hearts-icon--active');
    }
    return result;
  });

  const isEnergyCommunityOrPeer2Peer = computed(() => {
    return productionType.value == "eeg" || productionType.value == "p2p";
  });
</script>

<style lang="scss">
  .maps-popup {
    $popup-width: 500px;
    $img-width: 100px;
    $img-width-sm: 150px;
    $img-width-md: 200px;
    $text-color: $grey-darker;
    font-size: 0.75rem;
    color: $text-color;

    @media (min-width: $bp-ssm) {
      display: flex;
    }

    @media (min-width: $bp-md) {
      max-width: $popup-width;
      width: $popup-width;
      font-size: 0.875rem;
    }

    &__left {
      flex-shrink: 0;
      width: 100%;
      position: relative;

      @media (min-width: $bp-ssm) {
        padding-right: $spacer10;
        flex-basis: $img-width-sm;
        max-width: $img-width-sm;
      }

      @media (min-width: $bp-md) {
        flex-basis: $img-width-md;
        max-width: $img-width-md;
      }
    }

    &__right {
      flex-shrink: 1;
    }

    &__img {
      display: block;
      width: 100%;
      height: auto;
      max-width: 30vmin;
      margin-bottom: $spacer05;
      margin-left: auto;
      margin-right: auto;

      .gm-style & {
        // We need to override this google style.
        max-height: 150px;
        width: auto;
        max-width: 100%;
      }
    }

    &__title {
      color: $grey-mediumdark;
      font-weight: 500;
      line-height: $line-height-min;
      margin-bottom: $spacer10;
      font-size: $font-size-lg;
      word-wrap: break-word;
      text-align: center;
    }

    &__description {
      margin-bottom: $spacer10;
      white-space: pre-line;
      overflow-wrap: anywhere;

      a {
        font-weight: bold;
        color: $green-dark;
      }
    }

    &__contact {
      white-space: pre-line;
      position: relative;
      margin-top: 1rem;
      bottom: 0;

      > span {
        margin-top: $spacer05;
        margin-bottom: $spacer10;
        display: flex;
        align-items: center;
        gap: 0.3rem;
        font-size: smaller;
      }

      a {
        font-weight: bold;
        color: $green-dark;
      }
    }

    &__label {
      color: $grey-darker;
      font-size: $font-size-base;
    }

    &__grid-fee-discount {
      margin-bottom: $spacer10;
      display: flex;
      align-items: center;

      &-icon {
        width: 3rem;
        height: auto;
        margin-right: $spacer05;
      }

      &-text {
        margin: 0;
      }
    }

    &__production-type,
    &__capacity,
    &__price,
    &__energy-lines {
      margin-bottom: $spacer05;
      line-height: $line-height-min;
      font-size: $font-size-base;
    }

    &__energy-lines {
      font-style: normal;

      &-note {
        margin-top: $spacer05;
      }
    }

    &__available-connections {
      margin: $spacer05 0;
    }

    &__connections {
      display: flex;
      align-items: center;
      margin-top: $spacer10;

      &-text {
        margin-left: $spacer075;

        &-name {
          font-style: normal;
        }
      }

      .icon--positive,
      .icon--negative {
        margin-top: 0;
      }
    }

    &__is-producer {
      color: $color-negative;
      flex-basis: 1.5rem;
      flex-shrink: 0;
    }

    &__is-consumer {
      color: $color-positive;
      flex-basis: 1.5rem;
      flex-shrink: 0;
    }

    &__donates {
      color: $error-color;
      flex-basis: 1.5rem;
      flex-shrink: 0;
      margin-left: $spacer025;
    }

    &__hearts {
      flex-basis: 1.5rem;
      flex-shrink: 0;

      &-icon {

        &--active {
          color: $red;
        }
      }

      .icon {
        margin-right: $spacer025;
        vertical-align: middle;
      }

      &__button {
        background: white;
        border: 0;
        padding: 0;
        display: flex;
        align-items: center;

        &[disabled],
        &--disabled {
          cursor: default;
        }
      }
    }

    &__connect {
      @media (max-width: $bp-sm) {
        text-align: center;
      }

      &-button {
        width: 100%;

        &--donate,
        &--stop-donating {

          .icon--heart-outline {
            margin-right: $spacer025;
          }
        }

        &--donate {
          background: $error-color;
          color: $white;

          &:hover,
          &:focus,
          &:active {
            background: color.adjust($error-color, $lightness: -5%);
            color: $white;
          }
        }

        &--stop-donating {
          background: $grey-lighter;
          color: $grey-darker;

          &:hover,
          &:focus,
          &:active {
            background: $grey-light;
            color: $grey-darker;
          }
        }

        +.button {
          margin-top: $spacer025;
        }
      }
    }

    &__connect:has(> *) {
      margin-bottom: $spacer10;
    }

    &__connect-now {
      display: block;
      background: $community;
      margin-top: $spacer05;

      @include hover-focus-active {
        background: color.adjust($community, $lightness: -2%);
      }
    }

    &__prices {
      display: flex;
    }

    &__production-data {
      margin-top: $spacer05;

      &-table {
        margin-top: $spacer05;

        &__link a {
          color: $grey-darker;
          text-decoration: underline;
        }
      }
    }

    .price {
      margin-bottom: $spacer05;
      min-width: 100px;

      &__label {
        color: $body-color;
        font-size: 100%;
      }

      &__value {
        line-height: $line-height-min;
        font-weight: 400;

        @media (min-width: $bp-sm) {
          font-size: $font-size-lg;
        }
      }
    }

    .button--primary {
      width: 100%;

      @media (max-width: ($bp-sm - 1px)) {
        padding-left: $spacer075;
        padding-right: $spacer075;
      }
    }
  }
</style>
